import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { AUTH_PORT } from '@/app/core/auth/auth-port';
import { RECARGAR_DOCUMENTO } from '@/app/core/access/recargar-documento';
import { IDENTIDAD_STORAGE_KEY, IDENTIDADES_SINTETICAS, type IdentidadSinteticaLocal } from '@/app/features/catalogos-comerciales/models/identidades-sinteticas';
import { IDENTITY_CHANGED_EVENT } from '@/app/features/catalogos-comerciales/services/acceso-contexto.service';
import { SelectorIdentidadLocalComponent } from '@/app/features/catalogos-comerciales/ui/selector-identidad-local.component';
import { ProspectosContextoService } from '@/app/features/prospectos/services/prospectos-contexto.service';
import { NavIcons } from '@/app/shared/nav/nav-icons';
import { SafeHtmlPipe } from '@/app/shared/ui/pipes/safe-html.pipe';
import { ShellStore } from './shell.store';

export function resolveLogoutRedirect(
  environment: string,
  gatewayLoginUrl: string,
): string | null {
  const normalizedGatewayLoginUrl = gatewayLoginUrl.trim();
  return environment !== 'dev' && normalizedGatewayLoginUrl ? normalizedGatewayLoginUrl : null;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [SafeHtmlPipe, SelectorIdentidadLocalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="shell-header" role="banner" data-testid="shell-header">
      <div class="shell-header__row">
        <div class="shell-header__left">
          <button
            class="shell-header__menu"
            type="button"
            data-testid="shell-menu-toggle"
            (click)="store.toggleSidebar()"
            title="Alternar menú"
            aria-label="Alternar menú lateral"
            [attr.aria-expanded]="!store.sidebarCollapsed()"
          >
            <span [innerHTML]="icons.menu | safeHtml" aria-hidden="true"></span>
          </button>

          <div class="shell-header__brand">
            <span class="shell-header__logo-slot brand-logo-slot">
              <img src="/assets/logo-colmena-horizontal-240.png" width="240" height="78" alt="Colmena" class="shell-header__logo" />
            </span>
            <span class="shell-header__brand-name">Colmena</span>
            @if (env !== 'prod') {
              <span class="brand-badge" [attr.data-env]="env">{{ env }}</span>
            }
          </div>
        </div>

        <div class="shell-header__right">
          @if (showDevIdentityControl()) {
            <div class="identity-popover" data-testid="header-identity-popover">
              <button
                #identityButton
                class="identity-popover__trigger"
                type="button"
                [attr.aria-label]="'Identidad local: cambiar. Desarrollo local ' + selectedIdentityLabel()"
                aria-haspopup="dialog"
                [attr.aria-expanded]="identityPopoverOpen()"
                [attr.aria-controls]="identityPopoverOpen() ? 'header-identity-panel' : null"
                (click)="toggleIdentityPopover()"
                data-testid="selector-identidad-trigger"
              >
                <span class="identity-popover__eyebrow">Desarrollo local</span>
                <span class="identity-popover__label">{{ selectedIdentityLabel() }}</span>
                <span class="identity-popover__label identity-popover__label--corto" aria-hidden="true">{{ selectedIdentityShortLabel() }}</span>
              </button>

              @if (identityPopoverOpen()) {
                <div #identityPanel class="identity-popover__panel" id="header-identity-panel" role="dialog" aria-label="Desarrollo local · Identidad">
                  <app-selector-identidad-local
                    [identidades]="contextoService.identidades"
                    [selectedIdentityId]="selectedIdentityId()"
                    [mfaVerificado]="selectedMfa()"
                    (selectionChange)="onIdentitySelectionChange($event)"
                  />
                </div>
              }
            </div>
          }

          <div class="user-chip">
            <span class="user-avatar">{{ sessionAvatar() }}</span>
            <span class="user-name">{{ sessionLabel() }}</span>
          </div>
          <button class="btn-logout" type="button" (click)="logout()">Salir</button>
        </div>
      </div>
      <div class="shell-header__stripe" aria-hidden="true"></div>
      @if (anuncio()) {
        <p class="sr-only" role="status" data-testid="anuncio-identidad">Identidad cambiada a {{ anuncio() }}.</p>
      }
    </header>
  `,
  styles: [
    `
      :host {
        display: block;
        grid-area: header;
        min-width: 0;
        position: sticky;
        top: 0;
        z-index: var(--shell-header-z-index);
        background: var(--surface);
        --shell-identity-popover-z-index: 140;
      }

      .shell-header {
        background: var(--surface);
        border-bottom: 1px solid var(--border);
        height: var(--header-h);
        position: relative;
      }

      .shell-header__row {
        height: var(--shell-header-row-height);
        padding: 0 var(--shell-header-inline-padding);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--shell-header-gap);
      }

      .shell-header__left,
      .shell-header__right {
        display: flex;
        align-items: center;
      }

      .shell-header__left {
        gap: var(--shell-header-brand-gap);
        min-width: 0;
      }

      .shell-header__right {
        gap: var(--shell-header-actions-gap);
        flex: 1 1 auto;
        justify-content: flex-end;
        min-width: 0;
      }

      .identity-popover {
        position: relative;
        flex: none;
      }

      .identity-popover__trigger {
        display: grid;
        gap: 0.1rem;
        padding: calc(var(--esp-interno) / 2) calc(var(--esp-interno) * 0.9);
        border: 1px solid var(--info-borde);
        border-radius: var(--radius-pill);
        background: var(--primary-subtle);
        color: var(--text);
        text-align: left;
      }

      .identity-popover__trigger:focus-visible {
        outline: var(--focus-ring);
        outline-offset: 2px;
        box-shadow: var(--halo);
      }

      .identity-popover__eyebrow {
        font-size: var(--t-micro);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--primary);
      }

      .identity-popover__label--corto {
        display: none;
      }

      .identity-popover__label {
        font-size: var(--t-caption);
        font-weight: 600;
        white-space: nowrap;
      }

      .identity-popover__panel {
        position: absolute;
        top: calc(100% + 0.5rem);
        right: 0;
        z-index: var(--shell-identity-popover-z-index);
        width: min(26rem, calc(100vw - 2rem));
        max-height: calc(100dvh - var(--header-h) - 1rem);
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      .shell-header__stripe {
        height: var(--shell-header-stripe-height);
        background: var(--degradado-barra);
      }

      .shell-header__menu {
        width: var(--shell-header-control-size);
        height: var(--shell-header-control-size);
        border-radius: var(--radius-pill);
        border: 1px solid var(--border);
        background: var(--surface);
        color: var(--primary);
        display: grid;
        place-items: center;
        flex: none;
      }

      .shell-header__menu:hover {
        background: var(--celeste);
      }

      .shell-header__menu ::ng-deep svg {
        width: var(--shell-menu-icon-size);
        height: var(--shell-menu-icon-size);
        stroke: currentColor;
      }

      .shell-header__brand {
        display: flex;
        align-items: center;
        gap: var(--shell-header-brand-gap);
        min-width: 0;
      }

      .shell-header__logo-slot {
        width: var(--shell-header-logo-width);
        height: var(--shell-header-logo-height);
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        flex: none;
      }

      .shell-header__logo {
        display: block;
        height: var(--shell-header-logo-height);
        width: auto;
        max-width: 100%;
      }

      /* El nombre en texto solo aparece cuando el logo se oculta (móvil): en escritorio sería un duplicado del logo. */
      .shell-header__brand-name {
        display: none;
        font-weight: 700;
        font-size: var(--t-h3);
        color: var(--text);
        letter-spacing: 0.01em;
        line-height: 1;
      }

      .brand-badge {
        font-size: var(--t-micro);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: calc(var(--esp-interno) / 6) calc(var(--esp-interno) / 1.75);
        border-radius: var(--radius-pill);
        border: 1px solid transparent;
        color: var(--primary);
        background: var(--primary-subtle);
        border-color: var(--info-borde);
      }
      .brand-badge[data-env='qa'] {
        color: var(--success);
        background: var(--success-subtle);
        border-color: var(--ok-borde);
      }
      .brand-badge[data-env='uat'] {
        color: var(--warning);
        background: var(--warning-subtle);
        border-color: var(--warn-borde);
      }

      .user-chip {
        display: flex;
        align-items: center;
        gap: calc(var(--esp-interno) / 2);
        padding: calc(var(--esp-interno) / 4) calc(var(--esp-interno) / 1.4) calc(var(--esp-interno) / 4) calc(var(--esp-interno) / 4);
        background: var(--pagina);
        border-radius: var(--radius-pill);
        border: 1px solid var(--border);
        min-width: 0;
      }

      .user-avatar {
        width: var(--shell-header-avatar-size);
        height: var(--shell-header-avatar-size);
        background: var(--primary);
        border-radius: var(--radius-pill);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--t-caption);
        font-weight: 700;
        color: var(--surface);
        flex: none;
      }

      .user-name {
        font-size: var(--shell-header-font-size);
        font-weight: 500;
        color: var(--text);
        max-width: var(--shell-header-user-max-width);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .btn-logout {
        background: var(--surface);
        border: 1px solid var(--border);
        color: var(--muted);
        font-size: var(--shell-header-font-size);
        cursor: pointer;
        padding: calc(var(--esp-interno) / 3) var(--esp-interno);
        border-radius: var(--radius-pill);
        transition:
          color 150ms,
          border-color 150ms,
          background 150ms;
      }
      .btn-logout:hover {
        color: var(--danger);
        border-color: var(--danger);
        background: var(--danger-subtle);
      }

      @media (max-width: 768px) {
        .shell-header__row {
          padding: 0 var(--esp-contenedor);
        }

        .user-name {
          display: none;
        }

        .shell-header__logo-slot {
          display: none;
        }

        .shell-header__brand-name {
          display: inline;
        }

        .brand-badge {
          display: none;
        }

        .shell-header__left {
          flex-shrink: 0;
        }

        .identity-popover {
          flex: 0 1 auto;
          min-width: 0;
        }

        .identity-popover__trigger {
          min-width: 0;
          width: 100%;
        }

        .identity-popover__eyebrow,
        .identity-popover__label {
          display: block;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .identity-popover__label--corto {
          display: none;
        }

        .identity-popover__panel {
          position: fixed;
          top: calc(var(--header-h) - var(--shell-header-stripe-height) + 0.5rem);
          right: var(--esp-contenedor);
          left: var(--esp-contenedor);
          width: auto;
          max-height: calc(100dvh - var(--header-h) - 1rem);
          overflow-y: auto;
          overscroll-behavior: contain;
        }
      }

      @media (max-width: 480px) {
        .identity-popover__eyebrow {
          display: none;
        }

        .identity-popover__label:not(.identity-popover__label--corto) {
          display: none;
        }

        .identity-popover__label--corto {
          display: block;
          white-space: nowrap;
        }

        .identity-popover ~ .user-chip {
          display: none;
        }
      }
    `,
  ],
})
export class HeaderComponent {
  protected readonly store = inject(ShellStore);
  protected readonly icons = NavIcons;
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly config = inject(APP_CONFIG);
  private readonly authPort = inject(AUTH_PORT, { optional: true });
  private readonly recargarDocumento = inject(RECARGAR_DOCUMENTO);
  protected readonly contextoService = inject(ProspectosContextoService);
  private readonly identityButton = viewChild<ElementRef<HTMLButtonElement>>('identityButton');
  private readonly identityPanel = viewChild<ElementRef<HTMLDivElement>>('identityPanel');

  private static readonly ANUNCIO_IDENTIDAD_KEY = 'sdr.anuncio-identidad';

  protected readonly env = this.config.environment;
  protected readonly identityPopoverOpen = signal(false);
  protected readonly anuncio = signal('');
  protected readonly sessionLabel = signal(this.readSessionLabel());
  protected readonly selectedIdentityId = signal<IdentidadSinteticaLocal['corporateIdentityId'] | null>(this.leerIdentidadSeleccionada());
  protected readonly selectedMfa = signal(this.contextoService.getSelectedMfa());
  protected readonly sessionAvatar = computed(() => this.buildAvatar(this.sessionLabel()));
  protected readonly selectedIdentityLabel = computed(
    () => IDENTIDADES_SINTETICAS.find((identity) => identity.corporateIdentityId === this.selectedIdentityId())?.label ?? 'Sin sesión',
  );
  protected readonly selectedIdentityShortLabel = computed(() => this.selectedIdentityLabel().replace(/ sintético$/, ''));
  protected readonly showDevIdentityControl = computed(() => this.config.panelDesarrolloLocalVisible);

  constructor() {
    const anuncioPrevio = this.document.defaultView?.sessionStorage.getItem(HeaderComponent.ANUNCIO_IDENTIDAD_KEY);
    if (anuncioPrevio) {
      this.document.defaultView?.sessionStorage.removeItem(HeaderComponent.ANUNCIO_IDENTIDAD_KEY);
      this.anuncio.set(anuncioPrevio);
    }

    const syncSession = () => {
      this.sessionLabel.set(this.readSessionLabel());
      this.selectedIdentityId.set(this.leerIdentidadSeleccionada());
      this.selectedMfa.set(this.contextoService.getSelectedMfa());
    };
    const handlePointerDown = (event: PointerEvent) => this.closeOnOutsidePointer(event);
    const handleKeyDown = (event: KeyboardEvent) => this.closeOnEscape(event);

    window.addEventListener('storage', syncSession);
    window.addEventListener(IDENTITY_CHANGED_EVENT, syncSession);
    this.document.addEventListener('pointerdown', handlePointerDown);
    this.document.addEventListener('keydown', handleKeyDown);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('storage', syncSession);
      window.removeEventListener(IDENTITY_CHANGED_EVENT, syncSession);
      this.document.removeEventListener('pointerdown', handlePointerDown);
      this.document.removeEventListener('keydown', handleKeyDown);
    });
  }

  protected toggleIdentityPopover(): void {
    const abierto = !this.identityPopoverOpen();
    this.identityPopoverOpen.set(abierto);

    if (abierto) {
      setTimeout(() => this.identityPanel()?.nativeElement.querySelector<HTMLElement>('.selector-card__opcion[aria-pressed="true"], .selector-card__opcion')?.focus(), 0);
    }
  }

  protected onIdentitySelectionChange(event: {
    corporateIdentityId: 'corp-admin-01' | 'corp-agente-01' | 'corp-jefe-01' | 'corp-director-01' | 'corp-soporte-01';
    mfaVerificado: boolean;
  }): void {
    if (
      this.contextoService.hasSelectedIdentity() &&
      event.corporateIdentityId === this.selectedIdentityId() &&
      event.mfaVerificado === this.selectedMfa()
    ) {
      return;
    }

    this.selectedIdentityId.set(event.corporateIdentityId);
    this.selectedMfa.set(event.mfaVerificado);
    this.contextoService.setSelectedIdentity(event.corporateIdentityId, event.mfaVerificado);
    this.document.defaultView?.sessionStorage.setItem(HeaderComponent.ANUNCIO_IDENTIDAD_KEY, this.selectedIdentityLabel());
    this.recargarDocumento();
  }

  protected logout(): void {
    this.closeIdentityPopover();

    if (this.config.auth.modo === 'entra') {
      if (!this.authPort) {
        console.error('[HeaderComponent] modo entra sin AUTH_PORT registrado: no se puede cerrar la sesión.');
        return;
      }
      void this.authPort.cerrarSesion().catch((error: unknown) => {
        console.error('[HeaderComponent] no se pudo cerrar la sesión', error);
      });
      return;
    }

    this.document.defaultView?.sessionStorage.removeItem(IDENTIDAD_STORAGE_KEY);
    this.document.defaultView?.sessionStorage.removeItem(this.config.auth.tokenStorageKey);
    window.dispatchEvent(new CustomEvent(IDENTITY_CHANGED_EVENT));
    this.sessionLabel.set('Sin sesión');

    const gatewayRedirect = resolveLogoutRedirect(this.env, this.config.auth.gatewayLoginUrl);
    if (gatewayRedirect) {
      this.document.defaultView?.location.assign(gatewayRedirect);
      return;
    }

    this.recargarDocumento('/');
  }

  private leerIdentidadSeleccionada(): IdentidadSinteticaLocal['corporateIdentityId'] | null {
    return this.contextoService.hasSelectedIdentity() ? this.contextoService.getSelectedIdentity().corporateIdentityId : null;
  }

  private readSessionLabel(): string {
    // FE-86: la sesión es una cookie HttpOnly del backend; no hay una cuenta de la que leer un nombre.
    if (this.config.auth.modo === 'entra') {
      return 'Sesión activa';
    }

    const raw = this.document.defaultView?.sessionStorage.getItem(IDENTIDAD_STORAGE_KEY);
    if (!raw) {
      return 'Sin sesión';
    }

    try {
      const parsed = JSON.parse(raw) as { corporateIdentityId?: string };
      return (
        IDENTIDADES_SINTETICAS.find((identity) => identity.corporateIdentityId === parsed.corporateIdentityId)?.label
        ?? 'Sin sesión'
      );
    } catch {
      return 'Sin sesión';
    }
  }

  private closeOnOutsidePointer(event: PointerEvent): void {
    if (!this.identityPopoverOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    const button = this.identityButton()?.nativeElement;
    const panel = this.identityPanel()?.nativeElement;
    if (button?.contains(target) || panel?.contains(target)) {
      return;
    }

    this.closeIdentityPopover();
  }

  private closeOnEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.identityPopoverOpen()) {
      return;
    }

    event.preventDefault();
    this.closeIdentityPopover();
  }

  private closeIdentityPopover(): void {
    if (!this.identityPopoverOpen()) {
      return;
    }

    this.identityPopoverOpen.set(false);
    queueMicrotask(() => this.identityButton()?.nativeElement.focus());
  }

  private buildAvatar(label: string): string {
    if (label === 'Sin sesión') {
      return 'SS';
    }

    const initials = label
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');

    return initials || 'US';
  }
}
